export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export function formatCurrency(amount, currency = 'CAD') {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatDate(date) {
  return new Date(date).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateShort(date) {
  return new Date(date).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
  })
}

export function formatTimestamp(date) {
  return new Date(date).toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function daysAgo(date) {
  const diff = Date.now() - new Date(date).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

// Terminal statuses — an animal in one of these has left care, so the
// day counter is frozen at `outcome_date - spell_start`. Animals in any
// other status are still in care, so the counter ticks up every day.
const TERMINAL_STATUSES = new Set(['adopted', 'transferred', 'deceased'])

// Days in the animal's CURRENT in-care spell. Falls back to days since
// intake_date for legacy rows where current_spell_started_at wasn't seeded
// (animals that were already adopted before the column was added).
// Returns null if neither anchor exists.
//
// End-of-spell logic:
//   - Terminal status + outcome_date set: freeze at outcome_date - spell_start
//   - Otherwise: today - spell_start (live counter for in-care animals)
export function daysInCare(animal) {
  if (!animal) return null
  const anchor = animal.current_spell_started_at || animal.intake_date
  if (!anchor) return null
  const endMs = TERMINAL_STATUSES.has(animal.status) && animal.outcome_date
    ? new Date(animal.outcome_date).getTime()
    : Date.now()
  const diff = endMs - new Date(anchor).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

export function daysBetween(start, end) {
  const diff = new Date(end).getTime() - new Date(start).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export function getMonthRange(date = new Date()) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1)
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return { start, end }
}

export function getYearRange(date = new Date()) {
  const start = new Date(date.getFullYear(), 0, 1)
  const end = new Date(date.getFullYear(), 11, 31)
  return { start, end }
}

export const HOUR_CATEGORIES = [
  'meetings', 'founder', 'adoptions', 'foster', 'vetting', 'supplies',
  'events', 'newsletter', 'grants', 'graphic_design', 'adventures', 'marketing'
]

export const HOUR_CATEGORY_LABELS = {
  meetings: 'Meetings',
  founder: 'Founder',
  adoptions: 'Adoptions',
  foster: 'Foster',
  vetting: 'Vetting',
  supplies: 'Supplies',
  events: 'Events',
  newsletter: 'Newsletter',
  grants: 'Grants',
  graphic_design: 'Graphic Design',
  adventures: 'Adventures',
  marketing: 'Marketing',
}

// Warm, muted category chip palette (bg + text). Intentionally avoids bright
// red/orange so the Hours log reads as a neutral catalog instead of a field
// of error states.
export const HOUR_CATEGORY_COLORS = {
  meetings:      { bg: '#dbe4eb', text: '#3a4a55' }, // dusty sky
  founder:       { bg: '#e8d4a8', text: '#6b5423' }, // warm amber
  adoptions:     { bg: '#e8c7c0', text: '#6b3a30' }, // dusty rose
  foster:        { bg: '#c8d4be', text: '#3d4a2f' }, // sage green
  vetting:       { bg: '#dbc7b8', text: '#5a4533' }, // soft clay
  supplies:      { bg: '#dcb2a1', text: '#5c3424' }, // muted terracotta
  events:        { bg: '#d0c6d8', text: '#4a3d56' }, // dusty lavender
  newsletter:    { bg: '#e4d9c0', text: '#5e4f2d' }, // warm sand
  grants:        { bg: '#c5c8a8', text: '#4a4d30' }, // muted olive
  graphic_design:{ bg: '#b8cecc', text: '#2e4643' }, // dusty teal
  adventures:    { bg: '#e6cba0', text: '#66492a' }, // soft amber
  marketing:     { bg: '#c0d0dc', text: '#384a55' }, // muted blue
}

export const ANIMAL_STATUS_LABELS = {
  intake: 'Intake',
  medical_hold: 'Medical Hold',
  foster: 'Foster',
  adoptable: 'Adoptable',
  adopted: 'Placed',
  transferred: 'Transferred',
  returned_to_owner: 'Returned To Owner',
  deceased: 'Deceased',
}

export const ANIMAL_STATUS_PRIORITY = {
  medical_hold: 0,
  intake: 1,
  foster: 2,
  adoptable: 3,
  adopted: 4,
  transferred: 5,
  deceased: 6,
}

export const INCOME_CATEGORIES = ['Programs (Adoption Fees)', 'Donations', 'Grants', 'Fundraising']
export const EXPENSE_CATEGORIES = ['Veterinary Fees', 'Program Support', 'Fundraising', 'General & Admin']

// Format a phone number for display. North American 10-digit input
// renders as "(403) 880-7623"; 11 digits starting with 1 get the "1"
// stripped first ("1 403 880 7623" → 10 digits → same format). Anything
// else returns the original string so international formats or partial
// entries are displayed unchanged rather than mangled.
//
// Storage stays raw — strip non-digits before saving to the DB.
// See `stripPhone` below for the save-side counterpart.
export function formatPhone(raw) {
  if (!raw) return ''
  const digits = String(raw).replace(/\D/g, '')
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (ten.length !== 10) return String(raw)
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

export function stripPhone(raw) {
  if (!raw) return ''
  return String(raw).replace(/\D/g, '')
}
